/* ============================================================================
   pdp-lp-blends.js
   Expand-in-place behaviour for the PDP landing-page blend tiles.

   One blend open at a time (accordion): opening a second tile closes the
   first. With ten tiles in a five-wide grid, allowing several open at once
   pushes the grid around so much that the tile you clicked scrolls off screen.

   The heavy lifting is CSS Grid's — a panel is `grid-column: 1 / -1` and sits
   next to its own tile in the DOM, so the browser places it on a full-width
   row directly beneath that tile with no measuring here. This file only
   toggles state and manages focus.

   Delegated listeners on the grid, not per-tile handlers, so the section
   survives a theme-editor re-render of its blocks without rebinding.
   ========================================================================== */

(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';

  function panelFor(tile) {
    var id = tile.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /* How many tiles are on a row right now. Read from the resolved grid track
     list rather than hardcoded, so this follows the CSS breakpoints (2 up on
     mobile, 5 up from 750px) without knowing what they are. */
  function columnCount(grid) {
    var tracks = window.getComputedStyle(grid).gridTemplateColumns;
    if (!tracks || tracks === 'none') return 1;
    return tracks.split(' ').filter(Boolean).length;
  }

  /* Park the panel after the LAST tile of the row its owner sits on.
     Left in source order the panel breaks the row at the clicked tile, which
     strands the rest of that row as empty coloured cells — opening tile 1 of a
     5-wide grid left four blanks beside it. Moving it to the row boundary
     keeps the row intact and drops the panel cleanly underneath. */
  function positionPanel(grid, tile) {
    var panel = panelFor(tile);
    if (!panel) return;

    var tiles = Array.prototype.slice.call(grid.querySelectorAll('[data-blend-tile], .pdp-lp-blends__tile--inert'));
    var index = tiles.indexOf(tile);
    if (index === -1) return;

    var cols = columnCount(grid);
    var lastOfRow = Math.min(Math.ceil((index + 1) / cols) * cols - 1, tiles.length - 1);
    var anchor = tiles[lastOfRow];
    if (!anchor) return;

    // Insert after the anchor tile — and after any panel already sitting there.
    var ref = anchor.nextElementSibling;
    while (ref && ref.hasAttribute('data-blend-panel') && ref !== panel) {
      ref = ref.nextElementSibling;
    }
    if (panel !== ref) grid.insertBefore(panel, ref);
  }

  function close(tile) {
    var panel = panelFor(tile);
    if (!panel) return;
    tile.setAttribute('aria-expanded', 'false');
    tile.classList.remove(OPEN_CLASS);
    panel.hidden = true;
    panel.classList.remove(OPEN_CLASS);
  }

  function open(tile) {
    var panel = panelFor(tile);
    if (!panel) return;
    var grid = tile.closest('[data-blends-grid]');
    if (grid) positionPanel(grid, tile);
    tile.setAttribute('aria-expanded', 'true');
    tile.classList.add(OPEN_CLASS);
    panel.hidden = false;
    // Class is added on the next frame so the open transition has a
    // from-state to animate out of; setting hidden and the class in the same
    // frame makes it snap.
    requestAnimationFrame(function () {
      panel.classList.add(OPEN_CLASS);
    });
  }

  function closeAllIn(grid, except) {
    var tiles = grid.querySelectorAll('[data-blend-tile]');
    for (var i = 0; i < tiles.length; i++) {
      if (tiles[i] !== except && tiles[i].getAttribute('aria-expanded') === 'true') {
        close(tiles[i]);
      }
    }
  }

  /* The panel's "Nutrition Facts & Ingredients" button opens that blend's
     dialog. It dispatches the snippet's documented 'pdp-nutrition:open' event
     rather than calling showModal itself, so pdp-nutrition.js keeps ownership
     of body scroll-lock, backdrop clicks, ESC and focus return — and passes
     the opener so focus comes back to the right button out of ten. */
  function bindNutritionLinks(grid) {
    grid.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-blend-nutrition-open]');
      if (!btn || !grid.contains(btn)) return;

      var dialog = document.getElementById(btn.getAttribute('data-blend-nutrition-open'));
      if (!dialog) return;

      event.preventDefault();
      // Stop the grid's own click handler treating this as a tile toggle and
      // collapsing the panel out from under the modal.
      event.stopPropagation();

      dialog.dispatchEvent(new CustomEvent('pdp-nutrition:open', {
        detail: { opener: btn }
      }));
    });
  }

  function initGrid(grid) {
    if (grid.dataset.blendsBound === 'true') return;
    grid.dataset.blendsBound = 'true';

    grid.addEventListener('click', function (event) {
      var closeBtn = event.target.closest('[data-blend-close]');
      if (closeBtn) {
        var panel = closeBtn.closest('[data-blend-panel]');
        var owner = panel && document.getElementById(panel.getAttribute('aria-labelledby'));
        if (owner) {
          close(owner);
          // Return focus to the tile that opened it, or the close button
          // becomes a focus dead end once its panel is gone.
          owner.focus();
        }
        return;
      }

      var tile = event.target.closest('[data-blend-tile]');
      if (!tile || !grid.contains(tile)) return;

      if (tile.getAttribute('aria-expanded') === 'true') {
        close(tile);
      } else {
        closeAllIn(grid, tile);
        open(tile);
      }
    });

    bindNutritionLinks(grid);

    grid.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var scope = event.target.closest('[data-blend-panel]');
      if (scope) {
        var owner = document.getElementById(scope.getAttribute('aria-labelledby'));
        if (owner) {
          close(owner);
          owner.focus();
        }
        return;
      }
      var tile = event.target.closest('[data-blend-tile]');
      if (tile && tile.getAttribute('aria-expanded') === 'true') {
        close(tile);
      }
    });
  }

  function init() {
    var grids = document.querySelectorAll('[data-blends-grid]');
    for (var i = 0; i < grids.length; i++) initGrid(grids[i]);
  }

  /* Crossing the 750px breakpoint changes the column count, which moves where
     the row actually ends — an open panel would otherwise stay parked at the
     old boundary and tear the grid. */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var openTiles = document.querySelectorAll('[data-blend-tile][aria-expanded="true"]');
      for (var i = 0; i < openTiles.length; i++) {
        var grid = openTiles[i].closest('[data-blends-grid]');
        if (grid) positionPanel(grid, openTiles[i]);
      }
    }, 150);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section wholesale on a block change.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', function (event) {
    var tile = event.target.querySelector
      ? event.target.querySelector('[data-blend-tile]')
      : null;
    if (tile && tile.getAttribute('aria-expanded') === 'false') {
      var grid = tile.closest('[data-blends-grid]');
      if (grid) closeAllIn(grid, tile);
      open(tile);
    }
  });
})();
